// src/components/Tirage.js
import React, { useState, useEffect } from "react";
import {
  getBilletsOnce, ecouterLots, getStatutOnce,
  cloturerVentes, reouvrir, lancerTirage,
} from "../services/db";
import { useTombola } from "../contexts/TombolaContext";
import { useAuth } from "../contexts/AuthContext";
import { exporterGagnantsPDF } from "../services/exportPDF";

// Une ligne par lot attribué, dans l'ordre des rangs
function lignesGagnants(lots, billets) {
  return lots
    .filter((l) => l.gagnantBilletId)
    .sort((a, b) => (a.rang || 0) - (b.rang || 0))
    .map((lot) => {
      const b = billets.find((x) => x.id === lot.gagnantBilletId);
      return {
        rang: lot.rang || "",
        lot: lot.description || "",
        valeur: lot.valeur || "",
        offertPar: lot.offertPar || "",
        numero: lot.gagnantNumero || b?.numero || "",
        nom: b?.nom || lot.gagnantNomPublic || lot.gagnantNom || "",
        telephone: b?.telephone || lot.gagnantTelephone || "",
        email: b?.email || lot.gagnantEmail || "",
      };
    });
}

// CSV lisible directement par Excel en France (séparateur ; et BOM UTF-8)
function telechargerCSV(nomFichier, lignes, options) {
  const cols = [["rang", "Rang"], ["lot", "Lot"]];
  if (options.showValeur) cols.push(["valeur", "Valeur"]);
  cols.push(["offertPar", "Offert par"]);
  if (options.showNumeroBillet) cols.push(["numero", "N° billet"]);
  cols.push(["nom", "Gagnant"]);
  if (options.showTelephone) cols.push(["telephone", "Téléphone"]);
  if (options.showEmail) cols.push(["email", "Email"]);
  const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [cols.map(([, t]) => cell(t)).join(";"),
    ...lignes.map((l) => cols.map(([k]) => cell(l[k])).join(";"))].join("\r\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nomFichier;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function Tirage() {
  const { activeTombolaId, activeTombola, isPro } = useTombola();
  const { orgId } = useAuth();

  const [billets, setBillets] = useState([]);
  const [lots, setLots] = useState([]);
  const [statut, setStatut] = useState({ ventesClosees: false });
  const [loading, setLoading] = useState(false);
  const [tirage, setTirage] = useState(false);
  const [msg, setMsg] = useState(null);
  const [pdfOptions, setPdfOptions] = useState({
    showTelephone: true, showEmail: true,
    showNumeroBillet: true, showValeur: true,
  });
  const [showPdfOptions, setShowPdfOptions] = useState(false);

  const isArchived = activeTombola?.archived;

  useEffect(() => {
    if (!orgId || !activeTombolaId) return;
    const unsub = ecouterLots(orgId, activeTombolaId, setLots);
    charger();
    return unsub;
  }, [orgId, activeTombolaId]);

  async function charger() {
    if (!orgId || !activeTombolaId) return;
    const [b, st] = await Promise.all([
      getBilletsOnce(orgId, activeTombolaId),
      getStatutOnce(orgId, activeTombolaId),
    ]);
    setBillets(b);
    setStatut(st);
  }

  function showMsg(text, type = "success") {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  }

  const ventesClosees = statut?.ventesClosees || false;
  const lotsAttribues = lots.filter((l) => l.gagnantBilletId);
  const dejaTire = lotsAttribues.length > 0;
  const totalBillets = billets.length;

  async function handleCloture() {
    if (!window.confirm("Clôturer les ventes ? Les bénévoles ne pourront plus vendre de billets.")) return;
    try {
      await cloturerVentes(orgId, activeTombolaId);
      setStatut({ ventesClosees: true });
      showMsg("🔴 Ventes clôturées.");
    } catch { showMsg("Erreur.", "error"); }
  }

  async function handleReouvrir() {
    if (!window.confirm("Réouvrir les ventes ?")) return;
    try {
      await reouvrir(orgId, activeTombolaId);
      setStatut({ ventesClosees: false });
      showMsg("🟢 Ventes réouvertes.");
    } catch { showMsg("Erreur.", "error"); }
  }

  async function handleTirage() {
    if (lots.filter((l) => !l.gagnantBilletId).length === 0) {
      showMsg("Tous les lots ont déjà un gagnant.", "error"); return;
    }
    if (!window.confirm(`Lancer le tirage au sort pour ${lots.filter((l)=>!l.gagnantBilletId).length} lot(s) ?`)) return;
    setTirage(true); setLoading(true);
    try {
      const resultats = await lancerTirage(orgId, activeTombolaId);
      showMsg(`🎰 Tirage terminé ! ${resultats.length} gagnant(s) désigné(s).`);
      await charger();
    } catch (e) {
      // Messages explicites renvoyés par le serveur (quota Starter, lots déjà tirés…)
      showMsg(e?.message || "Erreur lors du tirage.", "error");
    }
    setLoading(false); setTirage(false);
  }

  const lotsParRang = [...lots].sort((a, b) => a.rang - b.rang);

  return (
    <div style={s.page}>
      <style>{`
        @keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes loading { 0%{width:0%} 50%{width:100%} 100%{width:0%;margin-left:100%} }
      `}</style>
      <h2 style={s.title}>🎰 Tirage au sort</h2>

      {msg && <div style={{...s.msg,...(msg.type==="error"?s.msgErr:s.msgOk)}}>{msg.text}</div>}

      {/* Statut */}
      <div style={s.statutCard}>
        <div style={s.statutRow}>
          <div style={s.statutItem}>
            <span style={s.statutNum}>{totalBillets}</span>
            <span style={s.statutLabel}>Billets vendus</span>
          </div>
          <div style={s.statutDivider} />
          <div style={s.statutItem}>
            <span style={s.statutNum}>{lots.length}</span>
            <span style={s.statutLabel}>Lots</span>
          </div>
          <div style={s.statutDivider} />
          <div style={s.statutItem}>
            <span style={{...s.statutNum,color:ventesClosees?"#c62828":"#2e7d32"}}>
              {ventesClosees ? "🔴" : "🟢"}
            </span>
            <span style={s.statutLabel}>{ventesClosees ? "Clôturées" : "Ouvertes"}</span>
          </div>
        </div>
      </div>

      {/* Actions clôture */}
      {!isArchived && (
        <div style={s.actionsRow}>
          {!ventesClosees ? (
            <button onClick={handleCloture} style={s.btnCloturer}>
              🔴 Clôturer les ventes
            </button>
          ) : (
            !dejaTire && (
              <button onClick={handleReouvrir} style={s.btnReouvrir}>
                🟢 Réouvrir les ventes
              </button>
            )
          )}

          {ventesClosees && lots.length > 0 && (
            <button onClick={handleTirage} disabled={loading}
              style={{...s.btnTirage,...(loading?s.btnDisabled:{})}}>
              {loading ? "🎰 Tirage en cours…" : "🎰 Lancer le tirage"}
            </button>
          )}
        </div>
      )}

      {/* Animation tirage */}
      {tirage && (
        <div style={s.tirageAnim}>
          <div style={s.tirageOrbs}>
            <div style={{...s.tirageOrb, animationDelay:"0s"}}>🥇</div>
            <div style={{...s.tirageOrb, animationDelay:"0.2s"}}>🎟️</div>
            <div style={{...s.tirageOrb, animationDelay:"0.4s"}}>🏆</div>
            <div style={{...s.tirageOrb, animationDelay:"0.6s"}}>🎁</div>
            <div style={{...s.tirageOrb, animationDelay:"0.8s"}}>⭐</div>
          </div>
          <div style={s.tirageBig}>🎰</div>
          <div style={s.tirageText}>Tirage en cours…</div>
          <div style={s.tirageSub}>Tirage sécurisé sur nos serveurs, journalisé</div>
          <div style={s.tirageBar}><div style={s.tirageBarFill} /></div>
        </div>
      )}

      {/* Résultats */}
      {lots.length > 0 && (
        <div style={s.resultats}>
          <div style={s.resultatsTitle}>
            {dejaTire ? "🏆 Résultats du tirage" : "📋 Lots à tirer"}
          </div>

          {lotsParRang.map((lot) => {
            const rang = lot.rang || 1;
            const colors = {
              1: { bg:"#fffbee", border:"#ffc107", color:"#b8860b", emoji:"🥇" },
              2: { bg:"#f8f8f8", border:"#bdbdbd", color:"#707070", emoji:"🥈" },
              3: { bg:"#fff5f0", border:"#e07040", color:"#a0522d", emoji:"🥉" },
            };
            const c = colors[rang] || { bg:"#f7f8fc", border:"#ddd", color:"#555", emoji:"🎁" };

            return (
              <div key={lot.id} style={{...s.lotCard, borderColor:c.border, ...(rang<=3?{borderWidth:"2px"}:{})}}>
                <div style={s.lotHeader}>
                  <span style={{...s.lotRang, background:c.bg, color:c.color, borderColor:c.border}}>
                    {c.emoji} {rang === 1 ? "1er" : `${rang}e`} prix
                  </span>
                  <div style={s.lotInfo}>
                    <span style={s.lotDesc}>{lot.description}</span>
                    {lot.valeur && <span style={s.lotVal}>{lot.valeur}</span>}
                    {lot.offertPar && <span style={s.offertPar}>🤝 {lot.offertPar}</span>}
                  </div>
                </div>

                {lot.gagnantBilletId ? (() => {
                  // Coordonnées lues dans les billets (privés), plus dans le lot (public)
                  const b = billets.find((x) => x.id === lot.gagnantBilletId);
                  const nom = b?.nom || lot.gagnantNomPublic || lot.gagnantNom;
                  const tel = b?.telephone || lot.gagnantTelephone;
                  const mail = b?.email || lot.gagnantEmail;
                  return (
                    <div style={{...s.gagnantBanner, background:c.bg, borderColor:c.border}}>
                      🏆 <strong>{nom}</strong> — billet {lot.gagnantNumero}
                      {tel && <span style={s.contact}> · 📞 {tel}</span>}
                      {mail && <span style={s.contact}> · ✉️ {mail}</span>}
                    </div>
                  );
                })() : (
                  <div style={s.enAttente}>
                    {ventesClosees ? "⏳ En attente du tirage" : "🔒 Ventes en cours"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Export PDF */}
      {dejaTire && (
        <div style={s.exportSection}>
          <div style={s.exportRow}>
            <button onClick={() => setShowPdfOptions(!showPdfOptions)} style={s.btnExport}>
              {showPdfOptions ? "▲ Exporter la liste" : "⬇️ Exporter la liste des gagnants"}
            </button>
          </div>
          {showPdfOptions && !isPro && (
            <div style={s.pdfOptions}>
              ⭐ Les exports PDF et Excel sont inclus dans l'offre Pro (29 €). Passez la tombola en Pro depuis l'onglet <strong>Paramètres</strong>.
            </div>
          )}
          {showPdfOptions && isPro && (
            <div style={s.pdfOptions}>
              <div style={s.pdfOptionsTitle}>Colonnes à inclure :</div>
              <div style={s.pdfOptionsGrid}>
                {[
                  { key:"showNumeroBillet", label:"N° billet" },
                  { key:"showTelephone", label:"Téléphone" },
                  { key:"showEmail", label:"Email" },
                  { key:"showValeur", label:"Valeur lot" },
                ].map(({ key, label }) => (
                  <label key={key} style={s.pdfOptionLabel}>
                    <input type="checkbox" checked={pdfOptions[key]}
                      onChange={(e) => setPdfOptions({...pdfOptions,[key]:e.target.checked})}
                      style={{marginRight:"6px"}} />
                    {label}
                  </label>
                ))}
              </div>
              <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
                <button onClick={() => {
                  try { exporterGagnantsPDF({ tombola: activeTombola, lignes: lignesGagnants(lots, billets), options: pdfOptions }); }
                  catch (e) { console.error(e); showMsg("Erreur lors de la création du PDF.", "error"); }
                }} style={s.btnExportPdf}>📄 PDF</button>
                <button onClick={() => {
                  const slug = (activeTombola?.nom || "tombola").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
                  telechargerCSV(`gagnants-${slug}.csv`, lignesGagnants(lots, billets), pdfOptions);
                }} style={s.btnExportPdf}>📊 Excel (CSV)</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Algo info */}
      <div style={s.algoInfo}>
        <span style={s.algoIcon}>🔒</span>
        <p style={s.algoText}>
          Le tirage est réalisé par nos serveurs, pas sur ce téléphone : personne ne peut l'influencer.
          Chaque billet a exactement la même chance de gagner, et chaque tirage est enregistré.
        </p>
      </div>
    </div>
  );
}

const s = {
  page:{maxWidth:"560px",margin:"0 auto"},
  title:{fontSize:"1.25rem",fontWeight:"700",color:"#1a1a2e",marginBottom:"1.25rem"},
  msg:{borderRadius:"10px",padding:"0.75rem 1rem",marginBottom:"1rem",fontSize:"0.9rem"},
  msgOk:{background:"#e8f5e9",color:"#1b5e20"},
  msgErr:{background:"#ffebee",color:"#b71c1c"},
  statutCard:{background:"#f7f8fc",borderRadius:"14px",border:"1.5px solid #e8e8e8",padding:"1.25rem",marginBottom:"1.25rem"},
  statutRow:{display:"flex",justifyContent:"space-around",alignItems:"center"},
  statutItem:{textAlign:"center"},
  statutNum:{display:"block",fontSize:"1.75rem",fontWeight:"900",color:"#1a1a2e"},
  statutLabel:{fontSize:"0.75rem",color:"#888",fontWeight:"600",textTransform:"uppercase"},
  statutDivider:{width:"1px",height:"40px",background:"#e0e0e0"},
  actionsRow:{display:"flex",gap:"10px",flexWrap:"wrap",marginBottom:"1.25rem"},
  btnCloturer:{flex:1,padding:"0.85rem",background:"#ffebee",color:"#c62828",border:"2px solid #ffcdd2",borderRadius:"12px",fontWeight:"700",cursor:"pointer",fontSize:"0.95rem"},
  btnReouvrir:{flex:1,padding:"0.85rem",background:"#e8f5e9",color:"#2e7d32",border:"2px solid #a5d6a7",borderRadius:"12px",fontWeight:"700",cursor:"pointer",fontSize:"0.95rem"},
  btnTirage:{flex:1,padding:"0.85rem",background:"linear-gradient(135deg,#667eea,#764ba2)",color:"#fff",border:"none",borderRadius:"12px",fontWeight:"800",cursor:"pointer",fontSize:"0.95rem",boxShadow:"0 4px 20px rgba(102,126,234,0.4)"},
  btnDisabled:{background:"#ccc",boxShadow:"none",cursor:"not-allowed"},
  tirageAnim:{textAlign:"center",padding:"2rem",background:"linear-gradient(135deg,#1a1a2e,#2d1b69)",borderRadius:"16px",marginBottom:"1.25rem",border:"2px solid #4c5ee8",boxShadow:"0 0 40px rgba(102,126,234,0.3)"},
  tirageOrbs:{display:"flex",justifyContent:"center",gap:"12px",marginBottom:"1rem"},
  tirageOrb:{fontSize:"1.5rem",animation:"bounce 1s ease-in-out infinite"},
  tirageBig:{fontSize:"4rem",marginBottom:"0.5rem",animation:"spin 0.5s linear infinite"},
  tirageText:{fontWeight:"800",color:"#fff",fontSize:"1.1rem",marginBottom:"4px"},
  tirageSub:{fontSize:"0.78rem",color:"rgba(255,255,255,0.5)",marginBottom:"1rem"},
  tirageBar:{background:"rgba(255,255,255,0.1)",borderRadius:"10px",height:"6px",overflow:"hidden"},
  tirageBarFill:{height:"100%",background:"linear-gradient(90deg,#667eea,#764ba2,#0099cc)",borderRadius:"10px",animation:"loading 1.5s ease-in-out infinite"},
  resultats:{marginBottom:"1.25rem"},
  resultatsTitle:{fontSize:"0.82rem",fontWeight:"700",color:"#aaa",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"0.75rem"},
  lotCard:{background:"#fff",border:"1.5px solid #eee",borderRadius:"12px",padding:"1rem",marginBottom:"8px"},
  lotHeader:{display:"flex",alignItems:"flex-start",gap:"10px",marginBottom:"8px"},
  lotRang:{padding:"4px 10px",borderRadius:"20px",fontSize:"0.78rem",fontWeight:"700",border:"1px solid",flexShrink:0},
  lotInfo:{flex:1},
  lotDesc:{display:"block",fontWeight:"600",color:"#1a1a2e",fontSize:"0.92rem"},
  lotVal:{background:"#f0f0f0",color:"#555",fontSize:"0.75rem",padding:"2px 8px",borderRadius:"4px",display:"inline-block",marginTop:"3px"},
  offertPar:{display:"inline-block",background:"#e8f5e9",color:"#2e7d32",fontSize:"0.75rem",padding:"2px 8px",borderRadius:"4px",marginTop:"3px",marginLeft:"4px"},
  gagnantBanner:{padding:"10px 12px",borderRadius:"8px",border:"1px solid",fontSize:"0.88rem",color:"#555",fontWeight:"500"},
  contact:{color:"#888"},
  enAttente:{fontSize:"0.82rem",color:"#aaa",fontStyle:"italic",padding:"6px 0"},
  exportSection:{marginBottom:"1.25rem"},
  exportRow:{display:"flex",gap:"10px",marginBottom:"8px"},
  btnExport:{padding:"0.6rem 1.25rem",background:"#f7f8fc",border:"1.5px solid #e0e0e0",borderRadius:"8px",cursor:"pointer",fontWeight:"600",fontSize:"0.85rem",color:"#555"},
  pdfOptions:{background:"#f7f8fc",border:"1.5px solid #e0e0e0",borderRadius:"10px",padding:"1rem"},
  pdfOptionsTitle:{fontSize:"0.82rem",fontWeight:"700",color:"#555",marginBottom:"8px"},
  pdfOptionsGrid:{display:"flex",flexWrap:"wrap",gap:"8px",marginBottom:"12px"},
  pdfOptionLabel:{display:"flex",alignItems:"center",fontSize:"0.85rem",color:"#333",background:"#fff",border:"1px solid #e0e0e0",borderRadius:"8px",padding:"5px 10px",cursor:"pointer"},
  btnExportPdf:{width:"100%",padding:"0.65rem",background:"linear-gradient(135deg,#667eea,#764ba2)",color:"#fff",border:"none",borderRadius:"8px",fontWeight:"700",cursor:"pointer"},
  styleTag: null,
  algoInfo:{display:"flex",gap:"10px",background:"#f0f4ff",borderRadius:"12px",padding:"1rem",border:"1px solid #c7d2ff"},
  algoIcon:{fontSize:"1.25rem",flexShrink:0},
  algoText:{margin:0,fontSize:"0.82rem",color:"#4c5ee8",lineHeight:1.6},
};
